import React, { useState, useEffect, useCallback, useRef } from 'react';
import { expensesService } from '../services/api';
import PeriodSelector, { PeriodFilter, defaultPeriodFilter } from './PeriodSelector';
import { monthNames } from '../utils/formatters';
import { campo, useEstadoDaCarga, EstadoDaLista, mensagemDoErroDeCarga } from '../design';
import { barraDoSinal } from './shared/lista/PecasDaLista';
import { useAviso } from './shared/Aviso';
import { FRASE_DA_COMPETENCIA } from '../utils/fluxoDeCaixa';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtPct = (v: number) => v.toFixed(2).replace('.', ',') + '%';

const fmtMonthShort = (m: string) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return `${(monthNames[parseInt(mo) - 1] || '').slice(0, 3)}/${y.slice(2)}`;
};

/**
 * O PADRÃO DA CENTRAL NO DRE (23/10/2026): superfície neutra e valor em branco, com o sinal. O verde do faturamento, o
 * vermelho das despesas, o âmbar do resultado financeiro e o roxo da distribuição eram decoração: o número do bloco e o
 * "-R$" já dizem o que é. O resultado (operacional ou líquido) leva a barra pelo sinal na 1ª célula: verde no positivo, vermelha no negativo. O valor não
 * quebra linha: o "R$" ia para uma linha e o número para outra nas colunas dos meses.
 */
const CARTAO = 'bg-central-cartao rounded-central border border-central-borda';
const NUM = 'whitespace-nowrap tabular-nums text-central-texto';

export default function DreView() {
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(() => {
    const now = new Date();
    const y = now.getFullYear();
    return { mode: 'month', months: [`${y}-01`], startDate: '', endDate: '' };
  });
  const [endMonth, setEndMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dreLoading, setDreLoading] = useState(false);

  const startMonth = periodFilter.months[0] || `${new Date().getFullYear()}-01`;

  // D4 (11/10/2026): a carga calava e não levava o período. Uma troca de período que falhava deixava a tabela do período
  // ANTERIOR sob os seletores novos, e o Excel saía com os dados velhos e o NOME do período novo. Agora cada leitura leva o
  // período; a tela só mostra (e só exporta) o que é do período dos seletores.
  const avisar = useAviso();
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const chave = `${startMonth}|${endMonth}`;
  const chavePedida = useRef(chave);
  chavePedida.current = chave;
  const [chaveDosDados, setChaveDosDados] = useState<string | null>(null);
  const dadosDoPeriodo = chaveDosDados === chave;
  const loadData = useCallback(async () => {
    setLoading(true);
    const pedida = `${startMonth}|${endMonth}`;
    await rodarCarga(async () => {
      const result = await expensesService.getDreData(startMonth, endMonth);
      if (chavePedida.current !== pedida) return;
      setData(result);
      setChaveDosDados(pedida);
    });
    setLoading(false);
  }, [startMonth, endMonth, rodarCarga]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    if (!data) return;
    const keys = new Set<string>();
    keys.add('receitas');
    keys.add('despesas');
    data.categories?.forEach((c: any) => keys.add(`cat_${c.name}`));
    setExpanded(keys);
  };

  const collapseAll = () => setExpanded(new Set());

  const handleExportExcel = () => {
    if (!data || !dadosDoPeriodo) return;
    const rows: string[][] = [];
    const months = data.months || [];
    const header = ['Conta', 'Total', '% AV', ...months.map(fmtMonthShort)];
    rows.push(header);

    // Revenue
    rows.push(['1 - FATURAMENTO BRUTO', `R$ ${fmt(data.totalRevenue)}`, '100,00%', ...months.map((m: string) => `R$ ${fmt(data.revenueByMonth[m] || 0)}`)]);

    // Expenses
    rows.push(['']);
    // Mesma estrutura da tela: só o bloco OPERACIONAL entra aqui, senão o
    // rótulo "Operacionais" cobriria distribuição de lucro e amortização.
    const b = data.blocks;
    const catsBy = (t: string) =>
      (data.categories || []).filter((c: any) => (c.treatment || 'operational') === t);
    const opT = b ? b.operational.total : data.totalExpenses;
    const opM = b ? b.operational.byMonth : data.expensesByMonth;

    rows.push(['2 - DESPESAS OPERACIONAIS', `R$ ${fmt(opT)}`, fmtPct(data.totalRevenue > 0 ? (opT / data.totalRevenue) * 100 : 0), ...months.map((m: string) => `R$ ${fmt(opM[m] || 0)}`)]);
    for (const cat of catsBy('operational')) {
      rows.push([`  ${cat.name}`, `R$ ${fmt(cat.total)}`, fmtPct(cat.percent), ...months.map((m: string) => `R$ ${fmt(cat.byMonth[m] || 0)}`)]);
    }

    rows.push(['']);
    rows.push(['RESULTADO OPERACIONAL', `R$ ${fmt(data.operationalResult ?? data.result)}`, fmtPct(data.operationalMarginPercent ?? data.marginPercent), ...months.map((m: string) => `R$ ${fmt((data.revenueByMonth[m] || 0) - (opM[m] || 0))}`)]);

    for (const [t, label] of [['financing', '3 - RESULTADO FINANCEIRO'], ['distribution', '4 - DISTRIBUIÇÃO DE LUCRO']] as const) {
      const total = b ? b[t].total : 0;
      if (!total) continue;
      const byMonth = b ? b[t].byMonth : {};
      rows.push(['']);
      rows.push([label, `R$ ${fmt(total)}`, fmtPct(data.totalRevenue > 0 ? (total / data.totalRevenue) * 100 : 0), ...months.map((m: string) => `R$ ${fmt(byMonth[m] || 0)}`)]);
      for (const cat of catsBy(t)) {
        rows.push([`  ${cat.name}`, `R$ ${fmt(cat.total)}`, fmtPct(cat.percent), ...months.map((m: string) => `R$ ${fmt(cat.byMonth[m] || 0)}`)]);
      }
    }

    if (b && (b.financing.total > 0 || b.distribution.total > 0)) {
      rows.push(['']);
      rows.push(['RESULTADO LÍQUIDO', `R$ ${fmt(data.netResult ?? data.result)}`, fmtPct(data.marginPercent), ...months.map((m: string) => `R$ ${fmt((data.revenueByMonth[m] || 0) - (data.expensesByMonth[m] || 0))}`)]);
    }

    const csv = rows.map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DRE_${startMonth}_${endMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setDreLoading(true);
    try {
      const blob = await expensesService.getDreReport(startMonth, endMonth);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DRE_${startMonth}_${endMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // D6: calava; o clique não fazia nada.
      avisar(`Não consegui gerar o PDF do DRE: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
    setDreLoading(false);
  };

  const months = data?.months || [];
  const showMonthColumns = months.length > 1 && months.length <= 12;

  // Blocos do DRE. Distribuição de lucro não é custo de operar e amortização de
  // dívida é financiamento — com as duas dentro, o "Resultado Operacional" não
  // media a operação (era 8,2% de margem quando a operação tem 46%).
  // Fallback para os campos antigos: se o backend ainda não devolver `blocks`,
  // a tela continua mostrando o número de antes em vez de quebrar.
  const blocks = data?.blocks;
  const opTotal = blocks ? blocks.operational.total : (data?.totalExpenses || 0);
  const opByMonth = blocks ? blocks.operational.byMonth : (data?.expensesByMonth || {});
  const opResult = data?.operationalResult ?? data?.result ?? 0;
  const opMargin = data?.operationalMarginPercent ?? data?.marginPercent ?? 0;
  const catsOf = (t: string) =>
    (data?.categories || []).filter((c: any) => (c.treatment || 'operational') === t);
  const LABELS: Record<string, string> = {
    financing: '3 - Resultado Financeiro',
    distribution: '4 - Distribuição de Lucro',
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">De:</span>
            <input
              type="month"
              value={startMonth}
              onChange={e => setPeriodFilter({ ...periodFilter, months: [e.target.value] })}
              className={`rounded-lg px-3 py-1.5 text-sm [color-scheme:dark] ${campo}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Ate:</span>
            <input
              type="month"
              value={endMonth}
              onChange={e => setEndMonth(e.target.value)}
              className={`rounded-lg px-3 py-1.5 text-sm [color-scheme:dark] ${campo}`}
            />
          </div>
          <button onClick={loadData} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-sm">Atualizar</button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={expandAll} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs">+ Expandir</button>
          <button onClick={collapseAll} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs">- Recolher</button>
          <button onClick={handleExportExcel} disabled={!data || !dadosDoPeriodo} title={!dadosDoPeriodo ? 'A tabela não é do período selecionado: o Excel sairia com o nome errado.' : undefined} className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1.5 disabled:opacity-50">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Excel
          </button>
          <button onClick={handleExportPdf} disabled={dreLoading || !data} className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1.5 disabled:opacity-50">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            {dreLoading ? 'Gerando...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* D5: o regime à vista. */}
      <p className="text-xs text-slate-400" data-testid="regime-do-dre">{FRASE_DA_COMPETENCIA}</p>

      {!dadosDoPeriodo ? (
        <EstadoDaLista carga={{ ...carga, estado: carga.estado === 'erro' ? 'erro' : 'carregando', atualizadoEm: null }} temDados={false} oQue="o DRE do período selecionado" onTentarDeNovo={loadData} vazio={null}>
          {null}
        </EstadoDaLista>
      ) : !data ? (
        <div className="text-center text-slate-500 py-12">Selecione o periodo e clique Atualizar</div>
      ) : (
        <div className={`${CARTAO} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-central-elevado">
              <tr>
                <th className="px-4 py-3 text-left text-slate-400 font-medium text-xs uppercase min-w-[300px]">Conta</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium text-xs uppercase w-[140px]">Total</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium text-xs uppercase w-[80px]">% AV</th>
                {showMonthColumns && months.map((m: string) => (
                  <th key={m} className="px-3 py-3 text-right text-slate-400 font-medium text-xs uppercase w-[110px]">{fmtMonthShort(m)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {/* 1 - FATURAMENTO BRUTO */}
              <tr
                className="bg-slate-700/20 cursor-pointer hover:bg-slate-700/40"
                onClick={() => toggleExpand('receitas')}
              >
                <td className="px-4 py-3 text-central-texto font-bold">
                  <span className="mr-2 text-xs text-central-texto-fraco">{expanded.has('receitas') ? '▼' : '▶'}</span>
                  1 - Faturamento Bruto
                </td>
                <td className={`px-4 py-3 text-right font-bold ${NUM}`}>R$ {fmt(data.totalRevenue)}</td>
                <td className={`px-4 py-3 text-right font-medium ${NUM}`}>100,00%</td>
                {showMonthColumns && months.map((m: string) => (
                  <td key={m} className={`px-3 py-3 text-right ${NUM}`}>R$ {fmt(data.revenueByMonth[m] || 0)}</td>
                ))}
              </tr>
              {expanded.has('receitas') && months.map((m: string) => (
                <tr key={`rev_${m}`} className="hover:bg-slate-700/20">
                  <td className="px-4 py-2 text-slate-300 pl-10 text-xs">{fmtMonthShort(m)} - Receitas</td>
                  <td className="px-4 py-2 text-right text-slate-300 text-xs whitespace-nowrap tabular-nums">R$ {fmt(data.revenueByMonth[m] || 0)}</td>
                  <td className="px-4 py-2 text-right text-slate-500 text-xs">
                    {data.totalRevenue > 0 ? fmtPct(((data.revenueByMonth[m] || 0) / data.totalRevenue) * 100) : '—'}
                  </td>
                  {showMonthColumns && months.map((m2: string) => (
                    <td key={m2} className="px-3 py-2 text-right text-slate-500 text-xs">{m2 === m ? `R$ ${fmt(data.revenueByMonth[m] || 0)}` : ''}</td>
                  ))}
                </tr>
              ))}

              {/* Spacer */}
              <tr><td colSpan={3 + (showMonthColumns ? months.length : 0)} className="py-1"></td></tr>

              {/* 2 - DESPESAS OPERACIONAIS */}
              <tr
                className="bg-slate-700/20 cursor-pointer hover:bg-slate-700/40"
                onClick={() => toggleExpand('despesas')}
              >
                <td className="px-4 py-3 text-central-texto font-bold">
                  <span className="mr-2 text-xs text-central-texto-fraco">{expanded.has('despesas') ? '▼' : '▶'}</span>
                  2 - Despesas Operacionais
                </td>
                {/* Só o bloco OPERACIONAL. Antes esta linha mostrava TODAS as
                    despesas com o rótulo "Operacionais", incluindo distribuição
                    de lucro e amortização de dívida. */}
                <td className={`px-4 py-3 text-right font-bold ${NUM}`}>-R$ {fmt(opTotal)}</td>
                <td className={`px-4 py-3 text-right font-medium ${NUM}`}>
                  -{data.totalRevenue > 0 ? fmtPct((opTotal / data.totalRevenue) * 100) : '—'}
                </td>
                {showMonthColumns && months.map((m: string) => (
                  <td key={m} className={`px-3 py-3 text-right ${NUM}`}>-R$ {fmt(opByMonth[m] || 0)}</td>
                ))}
              </tr>

              {expanded.has('despesas') && catsOf('operational').map((cat: any) => (
                <React.Fragment key={cat.name}>
                  <tr
                    className="hover:bg-slate-700/20 cursor-pointer"
                    onClick={() => toggleExpand(`cat_${cat.name}`)}
                  >
                    <td className="px-4 py-2 text-central-texto pl-8">
                      <span className="mr-2 text-xs text-slate-500">{expanded.has(`cat_${cat.name}`) ? '▼' : '▶'}</span>
                      {cat.name}
                    </td>
                    <td className={`px-4 py-2 text-right ${NUM}`}>-R$ {fmt(cat.total)}</td>
                    <td className="px-4 py-2 text-right text-central-texto-fraco text-xs whitespace-nowrap">-{fmtPct(cat.percent)}</td>
                    {showMonthColumns && months.map((m: string) => (
                      <td key={m} className="px-3 py-2 text-right text-central-texto-fraco text-xs whitespace-nowrap tabular-nums">
                        {cat.byMonth[m] ? `-R$ ${fmt(cat.byMonth[m])}` : ''}
                      </td>
                    ))}
                  </tr>
                  {expanded.has(`cat_${cat.name}`) && months.filter((m: string) => cat.byMonth[m] > 0).map((m: string) => (
                    <tr key={`${cat.name}_${m}`} className="hover:bg-slate-700/10">
                      <td className="px-4 py-1.5 text-slate-500 pl-14 text-xs">{fmtMonthShort(m)}</td>
                      <td className="px-4 py-1.5 text-right text-slate-500 text-xs whitespace-nowrap tabular-nums">R$ {fmt(cat.byMonth[m])}</td>
                      <td className="px-4 py-1.5 text-right text-slate-600 text-xs">
                        {data.totalRevenue > 0 ? fmtPct((cat.byMonth[m] / data.totalRevenue) * 100) : ''}
                      </td>
                      {showMonthColumns && months.map((m2: string) => (
                        <td key={m2} className="px-3 py-1.5 text-right text-slate-600 text-xs">{m2 === m ? `R$ ${fmt(cat.byMonth[m])}` : ''}</td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}

              {/* Spacer */}
              <tr><td colSpan={3 + (showMonthColumns ? months.length : 0)} className="py-1"></td></tr>

              {/* RESULTADO OPERACIONAL — receita menos SÓ o operacional. É o
                  número que responde "a operação se paga?". Antes esta linha
                  mostrava o resultado LÍQUIDO com este rótulo (8,2% de margem
                  quando a operação tem 46%). */}
              <tr className="bg-central-elevado border-t-2 border-central-borda" data-resultado="operacional">
                <td className={`px-4 py-4 font-bold text-base text-central-texto ${barraDoSinal(opResult)}`}>
                  RESULTADO OPERACIONAL
                </td>
                <td className={`px-4 py-4 text-right font-bold text-base ${NUM}`}>
                  R$ {fmt(opResult)}
                </td>
                <td className={`px-4 py-4 text-right font-medium ${NUM}`}>
                  {fmtPct(opMargin)}
                </td>
                {showMonthColumns && months.map((m: string) => {
                  const mResult = (data.revenueByMonth[m] || 0) - (opByMonth[m] || 0);
                  return (
                    <td key={m} className={`px-3 py-4 text-right font-medium ${NUM}`}>
                      R$ {fmt(mResult)}
                    </td>
                  );
                })}
              </tr>

              {/* Blocos que NÃO são custo de operar. Ficam DEPOIS do resultado
                  operacional, cada um com suas categorias. Só renderiza se
                  houver valor. */}
              {(['financing', 'distribution'] as const).map((t) => {
                const total = blocks ? blocks[t].total : 0;
                if (!total) return null;
                const byMonth = blocks ? blocks[t].byMonth : {};
                return (
                  <React.Fragment key={t}>
                    <tr className="bg-slate-700/20 cursor-pointer hover:bg-slate-700/40" onClick={() => toggleExpand(t)}>
                      <td className="px-4 py-3 font-bold text-central-texto">
                        <span className="mr-2 text-xs text-central-texto-fraco">{expanded.has(t) ? '▼' : '▶'}</span>
                        {LABELS[t]}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${NUM}`}>-R$ {fmt(total)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${NUM}`}>
                        -{data.totalRevenue > 0 ? fmtPct((total / data.totalRevenue) * 100) : '—'}
                      </td>
                      {showMonthColumns && months.map((m: string) => (
                        <td key={m} className={`px-3 py-3 text-right ${NUM}`}>
                          {byMonth[m] ? `-R$ ${fmt(byMonth[m])}` : ''}
                        </td>
                      ))}
                    </tr>
                    {expanded.has(t) && catsOf(t).map((cat: any) => (
                      <tr key={`${t}_${cat.name}`} className="hover:bg-slate-700/20">
                        <td className="px-4 py-2 text-slate-200 pl-8">{cat.name}</td>
                        <td className={`px-4 py-2 text-right ${NUM}`}>-R$ {fmt(cat.total)}</td>
                        <td className="px-4 py-2 text-right text-xs text-central-texto-fraco whitespace-nowrap">-{fmtPct(cat.percent)}</td>
                        {showMonthColumns && months.map((m: string) => (
                          <td key={m} className="px-3 py-2 text-right text-xs text-central-texto-fraco whitespace-nowrap tabular-nums">
                            {cat.byMonth[m] ? `-R$ ${fmt(cat.byMonth[m])}` : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}

              {/* RESULTADO LÍQUIDO — só faz sentido se houver bloco não-operacional */}
              {blocks && (blocks.financing.total > 0 || blocks.distribution.total > 0) && (
                <tr className="border-t border-central-borda" data-resultado="liquido">
                  <td className={`px-4 py-3 font-bold text-central-texto ${barraDoSinal(data.netResult ?? data.result)}`}>
                    RESULTADO LÍQUIDO
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${NUM}`}>
                    R$ {fmt(data.netResult ?? data.result)}
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${NUM}`}>
                    {fmtPct(data.marginPercent)}
                  </td>
                  {showMonthColumns && months.map((m: string) => {
                    const mr = (data.revenueByMonth[m] || 0) - (data.expensesByMonth[m] || 0);
                    return (
                      <td key={m} className={`px-3 py-3 text-right ${NUM}`}>
                        R$ {fmt(mr)}
                      </td>
                    );
                  })}
                </tr>
              )}

            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
